import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { projects } from '../data/projects';

// Mocking AnimatedSection locally to ensure it runs smoothly
const AnimatedSection = ({ children, delay = 0, animationType = "fadeUp" }) => {
  const variants = {
    fadeUp: { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } },
    scale: { hidden: { opacity: 0, scale: 0.95 }, visible: { opacity: 1, scale: 1 } },
  };

  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.6, delay: delay / 1000 }}
      variants={variants[animationType] || variants.fadeUp}
    >
      {children}
    </motion.div>
  );
};

const ProjectPortfolio = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isExpanded, setIsExpanded] = useState(false);

  const categories = [
    { label: 'ALL', value: 'all' },
    { label: 'COMMERCIAL', value: 'commercial' },
    { label: 'RESIDENTIAL', value: 'residential' },
    { label: 'MIXED-USE', value: 'mixed-use' },
    { label: 'HEALTHCARE', value: 'healthcare' },
    { label: 'EDUCATION', value: 'education' },
  ];

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setIsExpanded(false);
  };

  const filteredProjects = selectedCategory === 'all'
    ? projects
    : projects.filter((project) => project.category.includes(selectedCategory));

  const projectsToDisplay = isExpanded ? filteredProjects : filteredProjects.slice(0, 5);

  const handleProjectClick = (projectId) => {
    navigate(`/projects/${projectId}`);
  };

  const handleExplore = () => {
    setIsExpanded(true);
  };

  return (
    // Changed main background to light slate
    <div className="w-full min-h-screen bg-slate-50 text-slate-900 font-sans" style={{ fontFamily: "Monstserrat, proxima-nova, Helveti, sans-serif" }}>
      
      {/* Hero Section - Light Theme */}
      <section className="relative bg-white py-10 overflow-hidden border-b border-slate-100">
        <div className="absolute inset-0">
          <div className="absolute -top-32 left-1/2 transform -translate-x-1/2 w-[800px] h-[800px] bg-[#2a3890]/5 blur-[120px]" />
        </div>

        <div className="relative max-w-4xl mx-auto px-6 text-center space-y-8">
          <AnimatedSection animationType="fadeUp">
            <div className="space-y-10 w-full">
              <div className="text-4xl md:text-5xl font-semibold leading-tight mb-16 pt-10">
                <h2 className="text-4xl md:text-5xl font-semibold uppercase tracking-tight text-black mb-6">
                  MAJOR PROJECTS <span className="text-slate-300 font-light">&</span> PORTFOLIO'<span className='text-black uppercase- tracking-widest font-light'>s</span>
                </h2>
                <motion.div 
                  initial={{ width: 0 }}
                  whileInView={{ width: 150 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  className="h-1 bg-[#2a3890] mx-auto rounded-full mt-4"
                />
                <p className="text-lg md:text-xl text-slate-600 font-light max-w-4xl mx-auto leading-relaxed mt-4">
                  TM International Group has successfully completed numerous high-value construction projects across multiple 
                  sectors and geographic markets. The following represents a selection of our major completed projects demonstrating 
                  our capabilities in delivering complex, large-scale developments.
                </p>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Filter Section - Light Theme */}
      <section className="bg-slate-50  top-0 z-50 py-4 shadow-sm"> 
        <div className="max-w-7xl mx-auto px-4 md:px-[80px]"> 
          <div className="flex flex-col md:flex-row w-full overflow-hidden bg-white shadow-md border border-slate-200">
            {categories.map((category, index) => (
              <button
                key={category.value}
                onClick={() => handleCategoryChange(category.value)}
                className={`flex-1 px-4 py-3 text-sm md:text-base font-bold uppercase transition-all duration-200 whitespace-nowrap cursor-pointer
                  ${
                    index !== categories.length - 1 
                      ? 'border-b md:border-b-0 md:border-r border-slate-100' 
                      : ''
                  }
                  ${
                    selectedCategory === category.value
                      ? 'bg-[#2a3890] text-white'  // Selected: Blue BG, White Text
                      : 'bg-white text-slate-500 hover:bg-slate-50 hover:text-[#2a3890]' 
                  }
                `}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid - Light Theme */}
      <section className="py-12 bg-slate-50 px-6 md:px-10">
        <div className="max-w-7xl mx-auto px-0 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3">    
            {/* Project Cards */}
            {projectsToDisplay.map((project, index) => (
              <AnimatedSection
                key={project.id}
                animationType="scale"
                delay={index * 50}
              >
                <div
                  className="group relative w-full h-64 md:h-72 overflow-hidden cursor-pointer bg-white shadow-md hover:shadow-xl transition-all duration-300"
                  onClick={() => handleProjectClick(project.id)}
                >
                  {/* IMAGE */}
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-700 grayscale group-hover:grayscale-0 group-hover:scale-105"
                  />
                  {/* OVERLAYS */}
                  {/* Updated hover overlay color */}
                  <div className="absolute inset-0 bg-slate-900/10 group-hover:bg-[#2a3890]/20 transition-colors duration-300 mix-blend-multiply"></div>
                  
                  {/* HOVER EXPANDING BOX */}
                  <div className="absolute bottom-0 left-0 w-full bg-white/95 backdrop-blur-sm p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300 border-t border-slate-100">
                      <h4 className="text-lg font-bold uppercase text-slate-900 mb-1 line-clamp-1">{project.title}</h4>
                      <div className="flex justify-between text-xs text-slate-500 font-medium">
                        <span>{project.location}</span>
                        <span>{project.completed}</span>
                      </div>
                      <div className="mt-2 text-sm text-slate-600 line-clamp-2">
                        {project.description}
                      </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}

            {/* THE "EXPLORE" BOX */}
            {!isExpanded && filteredProjects.length > 5 && (
              <AnimatedSection animationType="scale" delay={300}>
                <div 
                   onClick={handleExplore}
                   className="w-full h-64 md:h-72 bg-[#2a3890] flex flex-col pt-10 p-6 cursor-pointer transition-transform group relative overflow-hidden shadow-lg"
                >
                    <div className="relative z-10 space-y-4">
                        <h2 className="text-4xl md:text-5xl font-black text-white leading-none uppercase">
                            EXPLORE <br/>
                            <span className='text-transparent' style={{ WebkitTextStroke: "1px white" }}>
                              PROJECTS</span>
                        </h2>
                        {/* Updated button to pop against blue bg */}
                        <button className="
                          w-fit bg-white text-[#2a3890] px-8 py-3 
                          uppercase tracking-wide font-bold hover:bg-black/40 hover:text-white
                          transition-colors duration-300
                        ">
                          VIEW MORE
                        </button>
                    </div>
                </div>
              </AnimatedSection>
            )}
          </div>

          {projectsToDisplay.length === 0 && (
            <div className="text-center py-20">
              <p className="text-slate-400 text-xl">No projects found.</p>
            </div>
          )}
        </div>
      </section>

      {/* Footer Section - with Background Image */}
      <section className="relative py-16 lg:py-20 text-white min-h-[500px] flex items-center">
         {/* Footer Background Image */}
         <div className="absolute inset-0 z-0">
            <img 
              src="https://images.unsplash.com/photo-1486325212027-8081e485255e?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80" 
              alt="Construction Skyline" 
              className="w-full h-full object-cover"
            />
         </div>

        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 w-full">
          <div className="bg-white/5 backdrop-blur-md border border-white/10 overflow-hidden flex flex-col-reverse lg:flex-row shadow-2xl">
            {/* Left column (Text Area) */}
            <div className="w-full lg:w-5/12 px-6 py-8 md:px-8 md:py-12 flex flex-col justify-center border-t lg:border-t-0 lg:border-r border-white/10">
              <div>
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white leading-tight">
                  Excellence in Construction
                </h2>
                {/* Updated Divider Color */}
                <div className="h-1 w-24 bg-[#2a3890] mt-6 rounded-full" />
              </div>
              
              <p className="text-slate-300 py-6 text-sm sm:text-base md:text-lg leading-relaxed text-left font-light">
                Delivering high-quality construction solutions across residential,
                commercial, healthcare, education, and specialized sectors with a
                strong focus on reliability and precision.
              </p>
              
              <div>
                <button
                  className="
                    w-fit bg-[#2a3890] text-white px-8 py-3 
                    uppercase tracking-wide font-bold hover:bg-white hover:text-[#2a3890]
                    transition-colors duration-300 
                  "
                  onClick={() => {
                    window.location.href = '/contact';
                  }}
                >
                  CONTACT US
                </button>
              </div>
            </div>

            {/* Right column (Stats Area) */}
            <div className="w-full lg:flex-1 p-6 lg:p-10 flex items-center">
              <div className="w-full grid grid-cols-2 gap-4 md:gap-6 text-center">
                <div className="bg-black/40 p-6 border border-white/5 hover:border-[#2a3890]/50 transition-colors">
                  <div className="text-3xl md:text-4xl font-bold text-white">10+ Years</div>
                  <div className="text-slate-300 text-sm mt-2 uppercase tracking-wider">
                    Industry Experience
                  </div>
                </div>
                <div className="bg-black/40 p-6 border border-white/5 hover:border-[#2a3890]/50 transition-colors">
                  <div className="text-3xl md:text-4xl font-bold text-white">15+</div>
                  <div className="text-slate-300 text-sm mt-2 uppercase tracking-wider">Major Projects</div>
                </div>
                <div className="bg-black/40 p-6 border border-white/5 hover:border-[#2a3890]/50 transition-colors">
                  <div className="text-3xl md:text-4xl font-bold text-white">8+</div>
                  <div className="text-slate-300 text-sm mt-2 uppercase tracking-wider">Industry Sectors</div>
                </div>
                <div className="bg-black/40 p-6 border border-white/5 hover:border-[#2a3890]/50 transition-colors">
                  <div className="text-3xl md:text-4xl font-bold text-white">100%</div>
                  <div className="text-slate-300 text-sm mt-2 uppercase tracking-wider">On-Time Delivery</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default ProjectPortfolio;